import { FormField } from '../../types/form-builder.types';
import { useFormSubmission } from '../../contexts/form-submission-context';
import { cn } from '@shondalai/ui-commons';
import { useFieldTranslation } from '../../hooks/useFormTranslations';
import { useConditionalLogic } from '../../hooks/useConditionalLogic';
import { useMemo } from 'react';

// Import field renderers
import {
  TextInputField,
  TextAreaField,
  SelectField,
  RadioField,
  CheckboxField,
  ToggleField,
  DateField,
  FileUploadField,
  RatingField,
  OpinionScaleField,
  LikertScaleField,
  NpsField,
  EmojiSentimentField,
  MatrixField,
  RatingMatrixField,
  HeadingField,
  ParagraphDisplayField,
  DividerField,
  TimerField,
  PaymentField,
  CaptchaField,
  SliderField,
  ColorField,
  CurrencyField,
  HtmlField,
  RankingField,
  ImageChoiceField,
  CountryField,
  CalculationField,
  SignatureField,
  AddressField,
} from './field-renderers';

interface FormFieldRendererProps {
  field: FormField;
  allFields?: FormField[];
  conditionalRules?: any[];
}

export function FormFieldRenderer({ field, allFields = [], conditionalRules = [] }: FormFieldRendererProps) {
  const { formData, setFieldValue, errors, form } = useFormSubmission();

  // Translate the field
  const translatedField = useFieldTranslation(field);

  // Get conditional rules from form or props
  const rules = conditionalRules.length > 0 ? conditionalRules : (form?.conditionalRules || []);

  // Get all fields from form sections if not provided
  const formFields = useMemo(() => {
    if (allFields.length > 0) return allFields;
    if (!form?.sections) return [];

    const fields: FormField[] = [];
    form.sections.forEach(section => {
      section.rows?.forEach(row => {
        row.fields?.forEach(f => fields.push(f));
      });
    });
    return fields;
  }, [allFields, form?.sections]);

  // Apply conditional logic
  const conditionalLogic = useConditionalLogic(rules, formData, formFields);

  // Check if field should be visible
  const isHidden = conditionalLogic.hiddenFields.has(field.id);
  const isDisabled = conditionalLogic.disabledFields.has(field.id);
  const isRequired = conditionalLogic.requiredFields.has(field.id);

  // Don't render if hidden
  if (isHidden) {
    return null;
  }

  const value = formData[field.id];
  const error = errors[field.id];

  // Create modified config with conditional logic applied
  const config = {
    ...translatedField.config,
    disabled: isDisabled || (translatedField.config as any).disabled,
    required: isRequired
  };

  // Update the translated field with modified config
  const finalField = {
    ...translatedField,
    config
  };

  // Layout/display fields that don't need labels (they render their own)
  const layoutFields = ['heading', 'paragraph-display', 'divider', 'html', 'payment', 'toggle', 'switch'];
  const isLayoutField = layoutFields.includes(field.type);

  // Render appropriate field based on type
  const renderField = () => {
    const commonProps = {
      field: finalField, // Use final field with conditional logic applied
      value,
      onChange: (newValue: any) => setFieldValue(field.id, newValue),
      error: error?.message,
    };

    switch (field.type) {
      // Basic Input Fields
      case 'text':
      case 'email':
      case 'tel':
      case 'url':
      case 'password':
      case 'number':
      case 'hidden':
        return <TextInputField {...commonProps} />;

      case 'textarea':
        return <TextAreaField {...commonProps} />;

      // Choice Fields
      case 'select':
        return <SelectField {...commonProps} />;

      case 'radio':
        return <RadioField {...commonProps} />;

      case 'checkbox':
        return <CheckboxField {...commonProps} />;

      case 'toggle':
      case 'switch':
        return <ToggleField {...commonProps} />;

      case 'ranking':
        return <RankingField {...commonProps} />;

      case 'image-choice':
        return <ImageChoiceField {...commonProps} />;

      // Date & Time Fields
      case 'date':
      case 'time':
      case 'datetime-local':
      case 'date-range':
        return <DateField {...commonProps} />;

      // File Fields
      case 'file':
      case 'image':
        return <FileUploadField {...commonProps} />;

      case 'signature':
        return <SignatureField {...commonProps} />;

      // Rating & Survey Fields
      case 'rating':
        return <RatingField {...commonProps} />;

      case 'opinion-scale':
        return <OpinionScaleField {...commonProps} />;

      case 'likert':
        return <LikertScaleField {...commonProps} />;

      case 'nps':
        return <NpsField {...commonProps} />;

      case 'emoji-sentiment':
        return <EmojiSentimentField {...commonProps} />;

      // Matrix Fields
      case 'matrix':
        return <MatrixField {...commonProps} />;

      case 'rating-matrix':
        return <RatingMatrixField {...commonProps} />;

      // Advanced Fields
      case 'address':
        return <AddressField {...commonProps} />;

      case 'country':
        return <CountryField {...commonProps} />;

      case 'range':
        return <SliderField {...commonProps} />;

      case 'color':
        return <ColorField {...commonProps} />;

      case 'currency':
        return <CurrencyField {...commonProps} />;

      case 'calculation':
        return <CalculationField {...commonProps} />;

      // Layout Fields
      case 'heading':
        return <HeadingField {...commonProps} />;

      case 'paragraph-display':
        return <ParagraphDisplayField {...commonProps} />;

      case 'divider':
        return <DividerField {...commonProps} />;

      case 'html':
        return <HtmlField {...commonProps} />;

      // Quiz Fields
      case 'timer':
        return <TimerField {...commonProps} />;

      // Payment Fields
      case 'payment':
        return <PaymentField {...commonProps} formData={formData} />;

      // Security Fields
      case 'captcha':
        return <CaptchaField {...commonProps} />;

      // Fallback
      default:
        return <TextInputField {...commonProps} />;
    }
  };

  // Layout fields render directly without wrapper
  if (isLayoutField) {
    return renderField();
  }

  // Regular fields with label and error display
  return (
    <div
      className={cn("easyforms-field-group", config.cssClass, error && "has-error")}
      data-field-id={field.id}
    >
      {/* Label */}
      {config.label && (
        <label
          htmlFor={field.id}
          className="easyforms-field-label"
        >
          {config.label}
          {config.required && <span className="easyforms-field-required">*</span>}
        </label>
      )}

      {/* Help Text */}
      {config.helpText && (
        <p className="easyforms-field-help">
          {config.helpText}
        </p>
      )}

      {/* Field */}
      {renderField()}

      {/* Error Message */}
      {error && (
        <p className="easyforms-field-error">
          {error.message}
        </p>
      )}
    </div>
  );
}
